import { ChevronRight, FileCheck2, GitBranch, MessageSquare, PencilLine } from 'lucide-react'
import { tokens } from '@/theme'
import type { ActivityEntry } from '@/domain/types'

/**
 * The merged activity feed.
 *
 * Replaces the Stage 8 history panel, which rendered temporal rows and
 * diffed them in the browser. Four sources now arrive pre-merged and
 * pre-diffed from `GET /api/initiatives/{id}/activity`: field changes, gate
 * clears, evidence and comments, newest first.
 *
 * Merging server-side is not a micro-optimisation. The diff needs the full
 * temporal row — twenty-odd columns, several of them 4000 characters — and
 * shipping all of that to the browser to throw most of it away was the old
 * shape's real cost.
 */

interface Props {
  entries: ActivityEntry[]
  /** Rendered when there is nothing yet. */
  emptyLabel?: string
}

export function ActivityFeed({ entries, emptyLabel }: Props) {
  if (entries.length === 0) {
    return (
      <div
        style={{
          padding: '32px 20px',
          textAlign: 'center',
          color: tokens.ink3,
          fontFamily: tokens.fontDisplay,
          fontSize: 13,
          background: tokens.paper2,
          border: `1px dashed ${tokens.line2}`,
          borderRadius: tokens.radiusMd,
        }}
      >
        {emptyLabel ?? 'Nothing has happened yet.'}
      </div>
    )
  }

  return (
    <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 12 }}>
      {entries.map((entry, index) => (
        <FeedRow key={feedKey(entry, index)} entry={entry} />
      ))}
    </ol>
  )
}

/**
 * Entries from different sources can share a timestamp to the millisecond
 * (a gate clear writes its log row and the initiative row in one
 * transaction), so the key mixes kind, id and index rather than trusting
 * `at` to be unique.
 */
function feedKey(entry: ActivityEntry, index: number): string {
  switch (entry.kind) {
    case 'comment':
      return `comment-${entry.commentId}`
    case 'evidence':
      return `evidence-${entry.evidenceId}`
    case 'gate':
      return `gate-${entry.gateCode}-${entry.at}`
    default:
      return `change-${entry.at}-${index}`
  }
}

function FeedRow({ entry }: { entry: ActivityEntry }) {
  const { icon, accent } = decorationFor(entry)

  return (
    <li style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 14, alignItems: 'start' }}>
      <div
        style={{
          width: 24,
          paddingTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <span
          style={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: tokens.paper,
            border: `1px solid ${accent}`,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </span>
        <span style={{ width: 1, flex: 1, background: tokens.line2, marginTop: 4 }} />
      </div>

      <div
        style={{
          padding: '12px 18px',
          background: tokens.paper,
          border: `1px solid ${tokens.line}`,
          borderLeft: `2px solid ${accent}`,
          borderRadius: tokens.radiusMd,
          fontFamily: tokens.fontDisplay,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 10,
            flexWrap: 'wrap',
            marginBottom: 6,
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: tokens.weightDemi,
              color: tokens.ink,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatTimestamp(entry.at)}
          </span>
          <span style={{ fontSize: 12, color: tokens.ink3 }}>
            by{' '}
            <span style={{ color: tokens.ink2, fontWeight: tokens.weightMedium }}>
              {entry.actorDisplayName || '—'}
            </span>
          </span>
          <Badge entry={entry} />
        </div>

        <FeedBody entry={entry} />
      </div>
    </li>
  )
}

function Badge({ entry }: { entry: ActivityEntry }) {
  const label =
    entry.kind === 'change'
      ? entry.isCreate
        ? 'Created'
        : 'Edited'
      : entry.kind === 'gate'
        ? `${entry.gateCode} · ${entry.outcome}`
        : entry.kind === 'evidence'
          ? 'Evidence'
          : entry.edited
            ? 'Comment · edited'
            : 'Comment'

  const { accent, wash, line } = badgeToneFor(entry)

  return (
    <span
      style={{
        fontSize: 9.5,
        fontWeight: tokens.weightDemi,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: accent,
        background: wash,
        border: `1px solid ${line}`,
        borderRadius: tokens.radiusSm,
        padding: '2px 7px',
      }}
    >
      {label}
    </span>
  )
}

function FeedBody({ entry }: { entry: ActivityEntry }) {
  switch (entry.kind) {
    case 'change':
      if (entry.isCreate) {
        return (
          <p style={{ margin: 0, fontSize: 13, color: tokens.ink2 }}>Initiative created at Idea.</p>
        )
      }
      return (
        <ul style={{ listStyle: 'none', display: 'grid', gap: 6, padding: 0, margin: 0 }}>
          {entry.changes.map(change => (
            <li
              key={change.field}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(96px, auto) 1fr',
                gap: 12,
                fontSize: 12,
                color: tokens.ink2,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: tokens.weightDemi,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: tokens.ink3,
                }}
              >
                {change.label}
              </span>
              <span>
                <span
                  style={{
                    color: tokens.ink4,
                    textDecoration: 'line-through',
                    textDecorationColor: tokens.line3,
                  }}
                >
                  {change.before || '—'}
                </span>{' '}
                <ChevronRight
                  size={11}
                  strokeWidth={1.6}
                  color={tokens.ink4}
                  style={{ verticalAlign: 'middle', margin: '0 2px' }}
                />{' '}
                <span style={{ color: tokens.ink, fontWeight: tokens.weightMedium }}>
                  {change.after || '—'}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )

    case 'gate':
      return (
        <div style={{ display: 'grid', gap: 5, fontSize: 13, color: tokens.ink2 }}>
          <span>
            {entry.outcome === 'Advance'
              ? `Advanced ${entry.fromStageCode} → ${entry.toStageCode}`
              : entry.outcome === 'StepBack'
                ? `Stepped back ${entry.fromStageCode} → ${entry.toStageCode}`
                : `${entry.outcome} at ${entry.fromStageCode}`}{' '}
            <span style={{ color: tokens.ink4 }}>· checklist v{entry.checklistVersion}</span>
          </span>
          {entry.reason && <span style={{ color: tokens.ink2 }}>{entry.reason}</span>}
          {entry.overrideReason && (
            <span style={{ color: tokens.statusRed, fontWeight: tokens.weightMedium }}>
              Override — {entry.overrideReason}
              {entry.overrideApproverDisplayName
                ? ` (approved by ${entry.overrideApproverDisplayName})`
                : ''}
            </span>
          )}
        </div>
      )

    case 'evidence':
      return (
        <div style={{ display: 'grid', gap: 4, fontSize: 13, color: tokens.ink2 }}>
          <span>
            <span style={{ color: tokens.ink, fontWeight: tokens.weightMedium }}>
              {entry.title}
            </span>{' '}
            <span style={{ color: tokens.ink4 }}>· {entry.evidenceKind}</span>
          </span>
          {entry.evidenceUrl && (
            <a
              href={entry.evidenceUrl}
              target="_blank"
              rel="noreferrer noopener"
              style={{ color: tokens.gold, fontSize: 12, wordBreak: 'break-all' }}
            >
              {entry.evidenceUrl}
            </a>
          )}
        </div>
      )

    case 'comment':
      return (
        <p
          style={{
            margin: 0,
            fontSize: 13,
            color: tokens.ink2,
            lineHeight: 1.55,
            whiteSpace: 'pre-wrap',
          }}
        >
          {entry.body}
        </p>
      )
  }
}

function decorationFor(entry: ActivityEntry): { icon: React.ReactNode; accent: string } {
  switch (entry.kind) {
    case 'gate':
      return {
        icon: <GitBranch size={11} strokeWidth={1.9} color={tokens.gold} />,
        accent: tokens.gold,
      }
    case 'evidence':
      return {
        icon: <FileCheck2 size={11} strokeWidth={1.9} color={tokens.statusTeal} />,
        accent: tokens.statusTeal,
      }
    case 'comment':
      return {
        icon: <MessageSquare size={11} strokeWidth={1.9} color={tokens.statusBlue} />,
        accent: tokens.statusBlue,
      }
    default:
      return {
        icon: <PencilLine size={11} strokeWidth={1.9} color={tokens.ink3} />,
        accent: tokens.line2,
      }
  }
}

function badgeToneFor(entry: ActivityEntry): { accent: string; wash: string; line: string } {
  switch (entry.kind) {
    case 'gate':
      return { accent: tokens.gold, wash: tokens.goldWash, line: tokens.goldLine }
    case 'evidence':
      return {
        accent: tokens.statusTeal,
        wash: tokens.statusTealWash,
        line: tokens.statusTealLine,
      }
    case 'comment':
      return {
        accent: tokens.statusBlue,
        wash: tokens.statusBlueWash,
        line: tokens.statusBlueLine,
      }
    default:
      return { accent: tokens.ink3, wash: tokens.paper3, line: tokens.line2 }
  }
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}
